import {
    Box,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    useOutsideClick
} from '@chakra-ui/react'
import ItemSearch from './ItemSearch'
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { FieldSearchProps } from './types'
import { ItemData } from '../../../../context/MenuContext'
import { useGet } from '../../../../hooks/useHttp'
import Close from '../../../../components/Icon/Close'
import Search from '../../../../components/Icon/Search'

export default function FieldSearch({ item, onSelect }: FieldSearchProps) {
    const ref = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [search, setSearch] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [index, setIndex] = useState(0)
    const { data } = useGet<ItemData[]>(
        `2>/api/items?search=${search}&limit=20`,
        []
    )

    useOutsideClick({
        ref,
        handler: () => {
            setIsOpen(false)
        }
    })

    async function searchHandler(ev: ChangeEvent<HTMLInputElement>) {
        if (item) {
            onSelect(null)
            setIsOpen(true)
            return
        }
        setSearch(ev.target.value)
        const value = ev.target.value.trim().toLowerCase()
        if (!value) {
            setIndex(0)
            return
        }

        setIsOpen(true)
        setIndex(_index =>
            data.result.length - 1 > _index ? _index : data.result.length - 1
        )
    }

    function keyHandler(ev: KeyboardEvent<HTMLInputElement>) {
        const items = data.result
        if (ev.code === 'ArrowUp') {
            setIndex(_index => (_index === 0 ? 0 : _index - 1))
        }
        if (ev.code === 'ArrowDown') {
            setIndex(_index => (_index === items.length ? _index : _index + 1))
        }
        if (ev.code === 'Enter' || ev.code === 'NumpadEnter') {
            setIsOpen(false)
            onSelect(items.find((_, i) => i === index) || null)
            setIndex(0)
        }
        if (ev.code === 'Escape') {
            setIsOpen(false)
            onSelect(null)
            setIndex(0)
        }
    }

    function clearHandler() {
        onSelect(null)
        if (ref.current) {
            ref.current.focus()
        }
    }

    useEffect(() => {
        setSearch('')
    }, [item])

    return (
        <Box position="relative" w="full" ref={ref}>
            <InputGroup>
                <InputLeftElement pointerEvents="none">
                    <Icon as={Search} />
                </InputLeftElement>
                <Input
                    placeholder="Búsqueda"
                    ref={inputRef}
                    bg="white"
                    onChange={searchHandler}
                    onKeyUp={ev => keyHandler(ev)}
                    value={item?.name || search}
                    onFocus={() => setIsOpen(true)}
                />
                <InputRightElement>
                    <Icon
                        as={Close}
                        fontSize="sm"
                        cursor="pointer"
                        display={item ? 'block' : 'none'}
                        onClick={clearHandler}
                    />
                </InputRightElement>
            </InputGroup>
            <Box
                position="absolute"
                top={12}
                zIndex={10}
                py={2}
                bg="white"
                borderRadius="md"
                boxShadow="md"
                w="full"
                minW="72"
                borderWidth="1px"
                display={isOpen ? 'block' : 'none'}
                maxH="96"
                overflow="auto"
            >
                {data.result.map((_item, i) => (
                    <ItemSearch
                        key={i}
                        item={_item}
                        hasStock={
                            !(_item.has_inventory_control && _item.stock <= 0)
                        }
                        isActive={i === index}
                        onSelect={() => {
                            onSelect(_item)
                            if (inputRef.current) {
                                inputRef.current.focus()
                            }
                            setIsOpen(false)
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}
